
import { from, of as observableOf, Subject } from 'rxjs'

import Button from '@mui/material/Button'
import ConfirmDialog from 'components/ConfirmDialog'
import CreateStreamForm, { FooterState } from 'components/CreateStreamForm/CreateStreamForm'
import { MergeStreamSelect } from 'components/CreateStreamForm/MergeStreamSelect'
import { PPDrawer } from 'components/PPDrawer'
import { FeedListItem } from 'components/SaveFeedDialog'
import { useMountEffect } from 'hooks/useMountEffect'
import { Stream } from 'interfaces'
import { useRef, useState } from 'react'
import { FormattedMessage } from 'react-intl'
import { useDispatch, useSelector } from 'react-redux'
import { catchError, mergeMap } from 'rxjs/operators'
import { addFeeds, deleteFeed } from 'services/content/feeds/actions'
import { userStreamsSelector } from 'services/content/selectors'
import { deleteStream, updateStream } from 'services/content/streams/actions'
import { checkFeatureAvailability } from 'services/product'
import { message } from 'services/snackbar'
import { FEATURE_FIND_PRIVATE_STREAMS } from 'shared/constants'
import { StoreThunkDispatch } from 'store/state'
import { ColorGradient } from 'utils/colors'
import styles from './EditStreamDialog.pcss'


interface EditStreamDialogOwnProps {
  stream: Stream
  open: boolean
  onClose: () => void
  onUpdated: (id: string) => void
}

type EditStreamDialogProps = EditStreamDialogOwnProps

export function EditStreamDialog(props: EditStreamDialogProps) {
  const dispatch = useDispatch<StoreThunkDispatch>()
  const { stream, onUpdated } = props
  const streams = useSelector(userStreamsSelector)
  const [deletedFeeds, setDeletedFeeds] = useState<Record<string, boolean>>({})
  const [loading, setLoading] = useState(false)
  const [footerState, setFooterState] = useState<FooterState>({ canSubmit: false, label: null, submit: () => {} })
  const imageUrls: string[] = stream.feeds.map(f => f.image).filter(Boolean) as string[]
  if (stream.featuredImageUrl) {
    imageUrls.unshift(stream.featuredImageUrl)
  }
  const deleteFeed$ = useRef<Subject<string>>(undefined)
  const deleteStream$ = useRef<Subject<string>>(undefined)
  const update$ = useRef<Subject<any>>(undefined)

  useMountEffect(() => {
    update$.current = new Subject()
    update$.current.pipe(
      mergeMap(data => {
        setLoading(true)
        return from(dispatch(updateStream(data)).unwrap())
          .pipe(catchError((e) => {
            dispatch(message('An error occured, please try again!', 'error'))
            return observableOf({ error: e })
          }))
      }))
      .subscribe((res: any) => {
        setLoading(false)
        if (!res.error) {
          dispatch(message('Stream updated!', 'success'))
          props.onClose()
        }
      })

    return () => update$.current?.unsubscribe()
  })

  useMountEffect(() => {
    deleteFeed$.current = new Subject()
    deleteFeed$.current.pipe(
      mergeMap(id => from(dispatch(deleteFeed({ feedId: id }))).pipe(catchError((e) => {
        dispatch(message('An error occured, please try again!', 'error'))
        return observableOf({ error: e, id })
      }))))
      .subscribe((response: any) => {
        if (response.error) {
          setDeletedFeeds(current => ({ ...current, [response.id]: false }))
        } else {
          dispatch(message('Feed removed!', 'success', undefined, true))
        }
      })

    return () => deleteFeed$.current?.unsubscribe()
  })

  useMountEffect(() => {
    deleteStream$.current = new Subject()
    deleteStream$.current.pipe(
      mergeMap(id => from(dispatch(deleteStream(id))).pipe(catchError((e) => {
        dispatch(message('There was an error deleting your stream, please try again!', 'error'))
        return observableOf({ error: e })
      }))))
      .subscribe((res: any) => {
        setLoading(false)
        if (!res.error) {
          dispatch(message('Stream deleted!', 'success'))
          props.onClose()
        }
      })

    return () => deleteStream$.current?.unsubscribe()
  })

  const feeds = stream.feeds.filter(f => !deletedFeeds[f.id])
  const mergeableStreams = streams.filter(s => !s.originalId && s.id !== stream.id)

  const update = (
    name: string,
    color: ColorGradient,
    image: File | null,
    isPrivate?: boolean,
    featuredImage?: string,
    secondaryImages?: string[]
  ) => {
    if (!name || !color) {
      return true
    }
    const colorString = `${color.from},${color.to}`
    const colorChanged = colorString !== stream.color
    const isRenamed = stream.title !== name
    const privateChanged = stream.isPrivate !== isPrivate

    if (privateChanged && isPrivate) {
      const privateStreamsAllowed = isPrivateStreamAllowed()
      if (!privateStreamsAllowed) {
        return true
      }
    }

    if (!isRenamed && !image && !privateChanged && !featuredImage && !colorChanged) {
      return true
    }

    update$.current?.next({
      id: stream.id,
      name,
      color: colorString,
      file: image,
      isPrivate,
      featuredImage,
      secondaryImages
    })
    return true
  }

  const removeFeed = (id: string) => {
    deleteFeed$.current?.next(id)
    setDeletedFeeds(current => ({ ...current, [id]: true }))
  }

  const removeStream = () => {
    setLoading(true)
    deleteStream$.current?.next(stream.id)
  }

  const isPrivateStreamAllowed = () => {
    return dispatch(checkFeatureAvailability(FEATURE_FIND_PRIVATE_STREAMS))
  }

  const mergeStream = (destinationStream: Stream) => {
    const currentFeedsIds = destinationStream.feeds.reduce((acc, f) => {
      acc[f.uniqueSource] = true
      return acc
    }, {} as Record<string, boolean>)
    // get unique feeds from baseStream and mergeStream
    const feedsToAdd = stream.feeds.filter(f => !currentFeedsIds[f.uniqueSource])
    if (feedsToAdd.length === 0) {
      dispatch(message(`All feeds of stream ${stream.title} are already present in ${destinationStream.title}`, 'info'))
      return
    }
    dispatch(addFeeds({ stream: destinationStream, feeds: feedsToAdd }))
      .unwrap()
      .then(() => {
        dispatch(message('Streams merged!', 'success'))
        onUpdated(destinationStream.id)
        setLoading(true)
        deleteStream$.current?.next(stream.id)
      })
      .catch((error) => {
        dispatch(message('Error saving feeds: ' + error.message, 'error'))
      })
  }

  const footer = (
    <div className={styles['footer-actions']}>
      <div className={styles['footer-actions-left']}>
        <MergeStreamSelect
          baseStream={stream}
          streams={mergeableStreams}
          onStreamSelected={mergeStream}
        />
        <ConfirmDialog
          message={<FormattedMessage id="find.my-streams.manage.delete-owned-title" />}
          variant="danger"
          confirmLabel={<FormattedMessage id="actions.delete" />}
        >
          {(confirm) => (
            <Button disabled={loading} onClick={confirm(removeStream)}>
              <FormattedMessage id="actions.delete" />
            </Button>
          )}
        </ConfirmDialog>
      </div>
      <div className={styles['footer-actions-right']}>
        <Button variant="text" onClick={props.onClose}>
          <FormattedMessage id="actions.cancel" />
        </Button>
        <Button
          variant="contained"
          color="primary"
          className={styles['btn-save']}
          disabled={!footerState.canSubmit}
          onClick={footerState.submit}
        >
          {footerState.label}
        </Button>
      </div>
    </div>
  )

  return (
    <PPDrawer
      open={props.open}
      onClose={props.onClose}
      size="md"
      data-testid="edit-stream-dialog"
      title={<FormattedMessage id="find.my-streams.manage.edit-window-title" />}
      footer={footer}
    >
      <div className={styles.body}>
        <CreateStreamForm
          externalChrome
          loading={loading}
          updateStream={stream}
          images={imageUrls || []}
          feeds={feeds}
          streams={streams}
          onFooterStateChange={setFooterState}
          onSubmit={update}
          onCancel={props.onClose}
          isPrivateOptionAllowed={isPrivateStreamAllowed}
        />
        {feeds.length > 0 && (
          <div className={styles['feeds-box']}>
            <h4 className={styles['feeds-list-title']}>{`Feeds: ${feeds.length}`}</h4>
            <ConfirmDialog
              message="Deleting the last feed in this stream will result in the stream being deleted."
              variant="danger"
              confirmLabel="Delete feed and stream"
            >
              {(confirm) => (
                <ul className={styles['feeds-list']}>
                  {feeds.map(feed => (
                    <FeedListItem
                      key={feed.id}
                      feed={feed}
                      onDelete={feeds.length === 1 ? confirm(removeStream) : removeFeed}
                    />
                  ))}
                </ul>
              )}
            </ConfirmDialog>
          </div>
        )}
      </div>
    </PPDrawer>
  )
}

export default EditStreamDialog
